import { Button, Container, InputLabel, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Info } from "@material-ui/icons";
import { App, Events, Workouts } from "app-interfaces";
import React from "react";
import { useTranslation } from "react-i18next";
import { RouteComponentProps, useHistory, withRouter } from "react-router-dom";
import { v4 as uuid } from "uuid";

import { useStateValue } from "../../config/AppContext";
import CatalogClass from "../../models/Catalog";
import { EventType } from "../../types/StaticTypes";
import { triggerDebuggerAndThrow } from "../../utils/debugging-utils";
import { formatDate } from "../../utils/time-utils";
import {
  SuperSetForm as AddExercise,
  SuperSetForm as AddSuperSet,
  ConfirmModal as Delete,
} from "../Modals";
import FaB from "../Utilities/FloatingActionButton";
import ExerciseSets from "./ExerciseSets";
import ExerciseSetsList from "./ExerciseSetsList";

const useStyles = makeStyles((theme) => ({
  container: {
    "& #FaB": {
      bottom: "60px!important",
    },
    "& .MuiInputLabel-root, & .MuiTypography-h5": {
      marginBottom: 15,
      fontSize: 17,
      fontWeight: 600,
      color: "#525252",
    },
    "& .MuiTypography-h5": {
      marginTop: 30,
      marginBottom: 30,
      "& button": {
        float: "right",
        marginTop: -12,
      },
    },
    "& .MuiInputBase-root": {
      height: 51,
      marginBottom: 15,
      padding: 20,
      fontSize: 16,
      "& .MuiOutlinedInput-notchedOutline": {
        border: "1px solid #dcdcdc",
      },
    },
    "& .no-sets-added": {
      display: "flex",
      flexDirection: "column",
      margin: "auto",
      padding: "60px 80px",
      height: "30vh",
      justifyContent: "center",
      textAlign: "center",
      // textTransform: 'capitalize',
      fontSize: 16,
      fontWeight: 500,
      color: "#595959",
      "& div": {
        width: "100%",
        "& svg": {
          height: 32,
          width: 32,
          fill: "#333333",
        },
      },
    },
    "& input": {
      width: "100%",
      border: "1px solid #dcdcdc",
      padding: "15px",
      fontSize: "16px",
      borderRadius: "3px",
    },
    "& textarea": {
      borderRadius: 3,
      padding: 15,
      width: "100%",
      height: 83,
      border: "1px solid #dcdcdc",
      backgroundColor: "transparent",
      fontSize: 16,
    },
    "& button": {
      height: 45,
      width: 135,
      borderRadius: 4,
      padding: "6px 12px",
      backgroundColor: "#000000",
      fontSize: 16,
      fontWeight: 500,
      textTransform: "capitalize",
      color: "#ffffff",
      [theme.breakpoints.down("md")]: {
        width: 125,
        fontSize: 15,
      },
      "&:hover": { backgroundColor: "#000000" },
      '&[type="submit"]': {
        backgroundColor: "var(--text-primary)",
        width: "100%",
        "&:hover": { backgroundColor: "var(--text-primary)" },
      },
    },
  },
}));

export const AddWorkoutWithRouter = withRouter(({ match }: AddWorkoutRouteProps) => (
  <WorkoutsAddComponent id={match.params.id} />
));

type AddWorkoutRouteProps = RouteComponentProps<{ id: string }> & Omit<Props, "id">;

type SetBoolean = (b: boolean) => void;
type Props = {
  id?: string;
  "session-workout"?: boolean;
  "handle-submit"?: (e: any) => void;
  showWorkout?: boolean;
  setShowWorkout?: (id: string) => void;
  showAddWorkout?: boolean;
  setShowAddWorkout?: (id: string | undefined) => void;
  showAdd?: boolean;
  setShowAdd?: SetBoolean;
  selectWorkout?: boolean;
  showSelectWorkout?: SetBoolean;
};
type EventWithWorkoutSession = Events.ResponseServerEvent<Events.SessionFullThatUsedToBePreview> & {
  session: { workout: Workouts.IWorkout };
};
export default function WorkoutsAddComponent(props: Props) {
  const classes = useStyles();
  const { t } = useTranslation();
  const history = useHistory();
  const { catalog, Methods } = useStateValue() as {
    catalog: CatalogClass;
    Methods: App.Methods;
  };
  const id = props.id;
  const workout = catalog.Workouts.find((el) => el.id === id);
  const initialState: Workouts.IWorkout = workout
    ? // Copy existing workout & update id:
      { ...workout, id: uuid() }
    : // Create fresh workout:
      {
        id: uuid(),
        name: "",
        description: "",
        notes: "",
        duration: 5,
        is_deleted: false,
        exercise_super_set: [] as Workouts.IExerciseSuperSet[],
      };

  const [catalogWorkout, setCatalogWorkout] = React.useState(initialState);
  const [clientWorkout, setClientWorkout] = React.useState<Workouts.IWorkout>();
  const [supersetList, setSupersetList] = React.useState<any[] | null>(null);
  const [exerciseList, setExerciseList] = React.useState<any[] | null>(null);
  const [superset, setSuperSet] = React.useState<Workouts.IExerciseSuperSet | null>(null);

  const [show, setShow] = React.useState(false);
  const [toggleAddSuperSet, setToggleAddSuperSet] = React.useState(false);
  const [toggleAddExercise, setToggleAddExercise] = React.useState(false);
  const [toggleDelete, setToggleDelete] = React.useState(false);

  const delSuperSetList = React.useCallback(() => {
    if (!superset) return;
    const superSetIndex = catalogWorkout.exercise_super_set.findIndex(
      (el) => el.id === superset.id
    );
    catalogWorkout.exercise_super_set.splice(superSetIndex, 1);
    return setSuperSet(null);
  }, [catalogWorkout, superset]);

  const showAddSupersetModal = React.useCallback(() => {
    if (!supersetList) return setToggleAddSuperSet(false);
    return setToggleAddSuperSet(true);
  }, [supersetList]);

  const showAddExerciseModal = React.useCallback(() => {
    if (!exerciseList) return setToggleAddExercise(false);
    return setToggleAddExercise(true);
  }, [exerciseList]);

  const showDeleteModal = React.useCallback(() => {
    if (!superset) return setToggleDelete(false);
    // return setToggleDelete(true);
    return delSuperSetList();
  }, [superset, delSuperSetList]);

  // Effects
  React.useEffect(showAddSupersetModal);
  React.useEffect(showAddExerciseModal);
  React.useEffect(showDeleteModal);
  React.useEffect(() => {
    if (id && !workout && !clientWorkout) {
      Methods.getEvents({
        ...{
          from: formatDate(new Date(new Date().getFullYear(), 0, 1), 3),
          to: formatDate(new Date(new Date().getFullYear(), 11, 31), 3),
          types: [EventType.Session],
          has_workout: true,
          limit: 20,
        },
        customer_id: undefined,
      }).then((_response) => {
        const response = _response as unknown as EventWithWorkoutSession[];
        const workoutIdMatches = (el: EventWithWorkoutSession) => el.session.workout.id === id;
        const event = response.find(workoutIdMatches);

        if (event) {
          const {
            session: { workout },
          } = event as any; // so weird, see above
          setClientWorkout(workout);
          setCatalogWorkout(workout);
        }
      });
    }
  }, [id, workout, clientWorkout, Methods]);

  const generateSuperSetList = (
    accum: JSX.Element[],
    superset: Workouts.IExerciseSuperSet,
    supersetIndex: number
  ) => {
    const exerciseList = superset.exercises.map((exercise, exerciseIndex) => (
      <ExerciseSets exercise={exercise} key={exerciseIndex} />
    ));
    accum = [
      ...accum,
      <ExerciseSetsList
        key={supersetIndex}
        superset={superset}
        supersetIndex={supersetIndex}
        exerciseList={exerciseList}
        handleSuperSet={handleSuperSet}
      />,
    ];
    return accum;
  };

  const handleSuperSet = (e: React.MouseEvent<HTMLLIElement>) => {
    e.preventDefault();
    const { id } = e.currentTarget;
    const targetSuperSet = catalogWorkout.exercise_super_set.find((el) => el.id === id);
    if (targetSuperSet) return setSuperSet(targetSuperSet);
  };

  const addSuperSetList = (e: React.FormEvent<HTMLFormElement>) => {
    if (!e.currentTarget.reportValidity()) return;
    e.preventDefault();
    e.stopPropagation();
    const formData = new FormData(e.currentTarget);
    const newSuperSetList = formData.get("exercises");
    if (newSuperSetList && typeof newSuperSetList === "string") {
      catalogWorkout.exercise_super_set.push({
        id: uuid(),
        exercises: JSON.parse(newSuperSetList),
      });
    }
    return setSupersetList(null);
  };
  const addExerciseList = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const formData = new FormData(e.currentTarget);
    const newExerciseList = formData.get("exercises");
    if (newExerciseList && typeof newExerciseList === "string") {
      const newExercises = [...JSON.parse(newExerciseList)];
      newExercises.forEach((exercise) => {
        exercise.id = uuid();
        catalogWorkout.exercise_super_set.push({
          id: uuid(),
          exercises: [exercise],
        });
      });
    }
    return setExerciseList(null);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const formData = new FormData(e.currentTarget);
    let params = {};
    const id = uuid();

    formData.forEach((value, name) => {
      if (name === "exercise_super_set") return;

      return Object.assign(params, { [name]: value });
    });

    let urlTop = window.location.href;
    let lastPart = urlTop.substr(urlTop.lastIndexOf("/") + 1);

    if (/schedules/?.test(window.location.href) || lastPart === "") {
      const triggerSubmit = props["handle-submit"];
      if (!triggerSubmit) return;
      const exercise_super_set = catalogWorkout.exercise_super_set;
      // .map((el) => el.id);
      Object.assign(params, { id, exercise_super_set });
      // console.log('exercise_ids',exercise_super_set )
      return triggerSubmit({ workout: params });
    }

    Object.assign(params, {
      id,
      exercise_super_set: catalogWorkout.exercise_super_set.map((el) => ({
        ...el,
        exercises: el.exercises.map((ele) => ({ ...ele, id: uuid() })),
        id: uuid(),
      })),
    });

    if (window.innerWidth > 700) {
      if (props.showAdd && props.setShowAdd) props.setShowAdd(false);
      if (props.showAddWorkout && props.setShowAddWorkout) props.setShowAddWorkout(undefined);
      if (props.selectWorkout && props.showSelectWorkout) props.showSelectWorkout(false);
      return Methods.addWorkouts(params).then(() => {
        return props.setShowWorkout && props.setShowWorkout(id);
      });
    } else if (window.innerWidth <= 700) {
      return Methods.addWorkouts(params).then(() => history.push(`/workouts/${id}`));
    }
  };

  return (
    <Container id="workouts-add" className={classes.container}>
      <form onSubmit={handleSubmit}>
        {/* Name */}
        <InputLabel htmlFor="name">{t("Workout name")}</InputLabel>
        <input
          // fullWidth
          required
          title=""
          id="name"
          name="name"
          type="text"
          // variant='outlined'
          placeholder={t("Workout name")}
          value={catalogWorkout.name}
          onChange={(e) =>
            setCatalogWorkout({
              ...catalogWorkout,
              name: e.currentTarget.value,
            })
          }
          onInvalid={(e) => {
            e.currentTarget.setCustomValidity(`${t("This field is required")}`);
          }}
          onInput={(e) => {
            e.currentTarget.setCustomValidity(``);
          }}
        />
        {/* Duration */}
        <InputLabel htmlFor="duration">
          {t("Duration")} ({t("Minutes")})
        </InputLabel>
        <input
          // fullWidth
          required
          title=""
          id="duration"
          name="duration"
          type="number"
          // variant='outlined'
          value={catalogWorkout.duration}
          onChange={(e) =>
            setCatalogWorkout({
              ...catalogWorkout,
              duration: parseInt(e.currentTarget.value),
            })
          }
          min={5}
          max={120}
          step={5}
          // inputProps={{
          //   min: 5,
          //   max: 120,
          //   step: 5
          // }}
          onInvalid={(e) => {
            e.currentTarget.setCustomValidity(`${t("This field is required")}`);
          }}
          onInput={(e) => {
            e.currentTarget.setCustomValidity(``);
          }}
        />
        {/* Description */}
        <InputLabel htmlFor="description">{t("Workout description")}</InputLabel>
        <textarea
          required
          title=""
          id="description"
          name="description"
          placeholder={t("Workout description")}
          value={catalogWorkout.description ? catalogWorkout.description : ""}
          onChange={(e) =>
            setCatalogWorkout({
              ...catalogWorkout,
              description: e.currentTarget.value,
            })
          }
          onInvalid={(e) => {
            e.currentTarget.setCustomValidity(`${t("This field is required")}`);
          }}
          onInput={(e) => {
            e.currentTarget.setCustomValidity(``);
          }}
        />
        {/* Notes */}
        <InputLabel htmlFor="notes">{t("Workout notes")}</InputLabel>
        <textarea
          id="notes"
          name="notes"
          placeholder={t("Workout notes")}
          value={catalogWorkout.notes ? catalogWorkout.notes : ""}
          onChange={(e) =>
            setCatalogWorkout({
              ...catalogWorkout,
              notes: e.currentTarget.value,
            })
          }
        />
        {/* Super Sets */}
        <Typography variant="h5">
          {/* {t("Exercise edit section")} */}
          {t("View workout info")}
          {/* <Button variant='contained' onClick={() => setSupersetList([])}>
            Add Super Set
          </Button> */}
        </Typography>
        <input
          id="exercise_super_set"
          name="exercise_super_set"
          type="text"
          style={{ display: "none" }}
          defaultValue={JSON.stringify(catalogWorkout.exercise_super_set)}
        />
        {catalogWorkout.exercise_super_set.length > 0 ? (
          <ul style={{ padding: "15px 0" }}>
            {catalogWorkout.exercise_super_set.reduce(generateSuperSetList, [])}
          </ul>
        ) : (
          <div className="no-sets-added">
            <div>
              <Info />
            </div>
            {/* {t('No exercises added yet. Please click the plus button to start adding exercises')} */}
            {t("No exercises added yet")}
          </div>
        )}

        {/* Actions */}
        <Button type="submit" variant="contained">
          {t("Save")}
        </Button>
      </form>
      <FaB
        show={show}
        onClick={() => setShow(!show)}
        options={[`${t("Add Exercises")}`, `${t("Add")} ${t("superset")}`]}
        onAddSuperset={() => setSupersetList([])}
        onAddExercise={() => setExerciseList([])}
        onAdd={triggerDebuggerAndThrow}
        onInvite={triggerDebuggerAndThrow}
      />
      <AddExercise
        show={toggleAddExercise}
        option={"Add Exercises"}
        addExercise={exerciseList}
        onSubmit={addExerciseList}
        onHide={() => setExerciseList(null)}
      />
      <AddSuperSet
        show={toggleAddSuperSet}
        option={"Add"}
        supersetList={supersetList}
        onSubmit={addSuperSetList}
        onHide={() => setSupersetList(null)}
      />
      <Delete
        show={toggleDelete}
        onSubmit={delSuperSetList}
        onHide={() => setSuperSet(null)}
        message={t("Are you sure you want to remove this exercise?")}
        title={t("Delete")}
      />
    </Container>
  );
}
